import { List, Props, ScrollToAlign } from './ListComponent';
import React from 'react';

function FixedSizeList<T>(props: Props<T>): List<T> {
  const getItemOffset = ({ itemSize }: Props<T>, index: number): number =>
    index * (itemSize as number);
  const getItemSize = ({ itemSize }: Props<T>): number => itemSize as number;
  const getEstimatedTotalSize = ({ itemCount, itemSize }: Props<T>): number =>
    (itemSize as number) * itemCount;

  const getStartIndexForOffset = ({ itemCount, itemSize }: Props<T>, offset: number): number =>
    Math.max(0, Math.min(itemCount - 1, Math.floor(offset / (itemSize as number))));

  const getOffsetForIndexAndAlignment = (
    { direction, height, itemCount, itemSize, layout, width }: Props<T>,
    index: number,
    align: ScrollToAlign,
    scrollOffset: number,
    _instanceProps: unknown,
    scrollbarSize: number
  ): number => {
    const isHorizontal: boolean = direction === 'horizontal' || layout === 'horizontal';
    const size: number = (isHorizontal ? width : height) as number;
    const lastItemOffset: number = Math.max(0, itemCount * (itemSize as number) - size);
    const maxOffset: number = Math.min(lastItemOffset, index * (itemSize as number));
    const minOffset: number = Math.max(
      0,
      index * (itemSize as number) - size + (itemSize as number) + scrollbarSize
    );
    let newAlign = align;

    if (newAlign === 'smart') {
      if (scrollOffset >= minOffset - size && scrollOffset <= maxOffset + size) {
        newAlign = 'auto';
      } else {
        newAlign = 'center';
      }
    }
    return getOffsetForIndex(newAlign, maxOffset, minOffset, size, lastItemOffset, scrollOffset);
  };

  function getOffsetForIndex(
    align: ScrollToAlign,
    maxOffset: number,
    minOffset: number,
    size: number,
    lastItemOffset: number,
    scrollOffset: number
  ): number {
    let offset: number;
    switch (align) {
      case 'start':
        offset = maxOffset;
        break;
      case 'end':
        offset = minOffset;
        break;
      case 'center':
        {
          // "Centered" offset is usually the average of the min and max.
          // But near the edges of the list, this doesn't hold true.
          const middleOffset = Math.round(minOffset + (maxOffset - minOffset) / 2);
          if (middleOffset < Math.ceil(size / 2)) {
            offset = 0; // near the beginning
          } else if (middleOffset > lastItemOffset + Math.floor(size / 2)) {
            offset = lastItemOffset; // near the end
          } else {
            offset = middleOffset;
          }
        }
        break;
      case 'auto':
      default:
        if (scrollOffset >= minOffset && scrollOffset <= maxOffset) {
          offset = scrollOffset;
        } else if (scrollOffset < minOffset) {
          offset = minOffset;
        } else {
          offset = maxOffset;
        }
    }
    return offset;
  }

  const getStopIndexForStartIndex = (
    { direction, height, itemCount, itemSize, layout, width }: Props<T>,
    startIndex: number,
    scrollOffset: number
  ): number => {
    const isHorizontal = direction === 'horizontal' || layout === 'horizontal';
    const offset = startIndex * (itemSize as number);
    const size = (isHorizontal ? width : height) as number;
    const numVisibleItems = Math.ceil((size + scrollOffset - offset) / (itemSize as number));
    return Math.max(
      0,
      Math.min(
        itemCount - 1,
        startIndex + numVisibleItems - 1 // -1 is because stop index is inclusive
      )
    );
  };

  const shouldResetStyleCacheOnItemSizeChange = true;

  const validateProps = ({ itemSize }: Props<T>): void => {
    if (process.env.NODE_ENV !== 'production') {
      if (typeof itemSize !== 'number') {
        throw Error(
          'An invalid "itemSize" prop has been specified. ' +
            'Value should be a number. ' +
            `"${itemSize === null ? 'null' : typeof itemSize}" was specified.`
        );
      }
    }
  };

  return new List<T>(
    props,
    getItemOffset,
    getEstimatedTotalSize,
    getItemSize,
    getOffsetForIndexAndAlignment,
    getStartIndexForOffset,
    getStopIndexForStartIndex,
    (): void => {},
    shouldResetStyleCacheOnItemSizeChange,
    validateProps
  );
}

FixedSizeList.prototype = React.Component.prototype;

export default FixedSizeList;
